<script setup>
import { ref, watch, computed, onBeforeMount } from "vue"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import Header from "@/js/partials/blog/article/Header.vue"
import Footer from "@/js/partials/Footer.vue"
import Loading from "@/js/components/loaders/Loading.vue"
import NotFound from "@/js/components/NotFound.vue"
import PageContent from "@/js/partials/blog/article/Content.vue"
import { useHead } from "@unhead/vue"
import { useI18n } from "vue-i18n"

const { t } = useI18n()
const loading = ref(true)
const store = useStore()
const route = useRoute()
const router = useRouter()

onBeforeMount(async () => {
  await fetchEntryAndUseHead()
})

const fetchEntryAndUseHead = async () => {
  await fetchEntry()
  useHead({
    title: `${entry.value.fields.metaTags?.fields.title} - Blog`,
    link: [
      {
        rel: "canonical",
        href: `${import.meta.env.VITE_BASE_URL}${route.path}`,
      },
    ],
    meta: [
      ...(!!entry.value.fields.metaTags?.fields.description
        ? [
            {
              name: "description",
              content: entry.value.fields.metaTags?.fields.description,
            },
          ]
        : []),
      ...(!!entry.value.fields.metaTags?.fields.ogTitle
        ? [
            {
              name: "og:title",
              content: entry.value.fields.metaTags?.fields.ogTitle,
            },
          ]
        : []),
      ...(!!entry.value.fields.metaTags?.fields.ogDescription
        ? [
            {
              name: "og:description",
              content: entry.value.fields.metaTags?.fields.ogDescription,
            },
          ]
        : []),
      ...(!!entry.value.fields.metaTags?.fields.ogImage
        ? [
            {
              name: "og:image",
              content:
                entry.value.fields.metaTags?.fields.ogImage.fields.file.url,
            },
          ]
        : []),
      ...(!!entry.value.fields.metaTags?.fields.ogType
        ? [
            {
              name: "og:type",
              content: entry.value.fields.metaTags?.fields.ogType,
            },
          ]
        : []),
    ],
    script: entry.value.fields.structuredMeta
      ? [
          {
            type: "application/ld+json",
            textContent: JSON.stringify(entry.value.fields.structuredMeta),
          },
        ]
      : [],
  })
}
const entry = computed(() =>
  store.getters["content/getEntryBySlug"](route.params.slug)
)
watch(() => route.params.slug, fetchEntryAndUseHead)

async function fetchEntry() {
  try {
    loading.value = true
    let entry = await store.dispatch("content/fetchEntryBySlug", {
      type: "article",
      slug: route.params.slug,
      tags: ["blogArticle"],
    })
    if (!entry) await router.push({ name: "PageNotFound" })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex flex-col min-h-screen overflow-hidden">
    <!-- Site header -->
    <Header />

    <!-- Page content -->
    <main class="grow pt-16 md:pt-20">
      <div class="flex flex-col min-h-screen overflow-hidden py-4 px-4">
        <Loading v-if="loading" class="h-screen" />
        <NotFound v-else-if="!entry" />
        <PageContent v-else :entry="entry" />
      </div>
    </main>

    <!-- Site footer -->
    <Footer />
  </div>
</template>
